import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useLanguage, useTranslation } from '../i18n';
import { apiClient } from '../services/api';
import { MeditationCard } from './MeditationCard';
import { PracticeCalendar } from './PracticeCalendar';
import { MeditationReminderCard } from './MeditationReminderCard';
import type { Meditation } from '../types';
import './HomePage.css';

export const HomePage: React.FC = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = useTranslation();
  const [todayMeditation, setTodayMeditation] = useState<Meditation | null>(null);
  const [popularMeditations, setPopularMeditations] = useState<Meditation[]>([]);
  const [sleepMeditations, setSleepMeditations] = useState<Meditation[]>([]);
  const [loading, setLoading] = useState(true);

  // Reload meditations when language changes
  useEffect(() => {
    loadMeditations();
  }, [language]);

  const loadMeditations = async () => {
    try {
      setLoading(true);
      
      // Get all active meditations
      const response = await apiClient.getMeditations();
      const allMeditations = response.data;

      if (allMeditations.length > 0) {
        // «Популярное сегодня» — только бесплатные медитации (Соня 27.07).
        // Если бесплатных нет — секция просто не показывается (todayMeditation=null).
        const freeMeditations = allMeditations.filter((m: Meditation) => m.is_free);
        if (freeMeditations.length > 0) {
          // Meditation of the day based on day of year
          const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000);
          const todayIndex = dayOfYear % freeMeditations.length;
          setTodayMeditation(freeMeditations[todayIndex]);
        }

        // Popular now: 3 random meditations
        const shuffled = [...allMeditations].sort(() => 0.5 - Math.random());
        setPopularMeditations(shuffled.slice(0, 3));
      }

      // Get sleep meditations
      const sleepResponse = await apiClient.getMeditations('sleep');
      setSleepMeditations(sleepResponse.data);
    } catch (error) {
      console.error('Failed to load meditations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = (): string => {
    const hour = new Date().getHours();
    if (hour < 12) return t.home.goodMorning;
    if (hour < 18) return t.home.goodAfternoon;
    return t.home.goodEvening;
  };

  return (
    <div className="page-container home-page">
      {/* Greeting Section */}
      <div className="greeting-section">
        <h1 className="greeting-text">{getGreeting()}, {user?.first_name || t.common.friend}!</h1>
      </div>

      {/* Journal Prompt */}
      <div className="journal-prompt-card">
        <h2 className="journal-prompt-title">{t.home.howAreYou}</h2>
        <p className="journal-prompt-subtitle">{t.home.trackMood}</p>
        <button 
          className="journal-prompt-button"
          onClick={() => navigate('/journal/add')}
        >
          {t.home.shareMood}
        </button>
      </div>

      {/* Напоминание о медитации (Sofa 24.09) */}
      <MeditationReminderCard />

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          {t.home.loadingMeditations}
        </div>
      ) : (
        <>
          {/* Today's Featured Meditation */}
          {todayMeditation && (
            <div className="section">
              <h2 className="section-title">{t.home.popularToday}</h2>
              <div className="featured-meditation">
                <MeditationCard meditation={todayMeditation} />
              </div>
            </div>
          )}

          {/* Popular Now */}
          {popularMeditations.length > 0 && (
            <div className="section">
              <h2 className="section-title">{t.home.popularNow}</h2>
              <div className="meditations-scroll">
                {popularMeditations.map((meditation) => (
                  <MeditationCard 
                    key={meditation.id} 
                    meditation={meditation} 
                  />
                ))}
              </div>
            </div>
          )}

          {/* Sleep Meditations */}
          {sleepMeditations.length > 0 && (
            <div className="section">
              <h2 className="section-title">{t.home.forSleep}</h2>
              <div className="meditations-scroll">
                {sleepMeditations.map((meditation) => (
                  <MeditationCard 
                    key={meditation.id} 
                    meditation={meditation} 
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Practice Calendar */}
      <div className="section">
        <h2 className="section-title">{t.home.practiceCalendar}</h2>
        <PracticeCalendar />
      </div>

      {/* Daily Wisdom */}
      <div className="section">
        <div className="wisdom-card">
          <div className="wisdom-icon">🕊️</div>
          <p className="wisdom-text">
            {t.home.wisdomQuote}
          </p>
        </div>
      </div>
    </div>
  );
};
